import { Link, useRouterState } from '@tanstack/react-router';
import { useAuthStore } from '../store/auth.ts';
import { canFinance } from '../lib/roles.ts';
import { Icon } from './Icon.tsx';
import { t } from '@/lib/strings';

type SideItem = {
  to: string;
  label: string;
  icon: string;
  matchPrefix?: string;
};

// Google-Drive-style navigation drawer (desktop): an elevated "new requisition"
// button, then full-width destination rows (icon + label) with a tonal pill
// highlight on the active row. Sits under the top app bar (top-16). Mirrors
// WapMeet's Sidebar; RTL so it hugs the inline-start (right) edge. The brand,
// identity and theme/sign-out controls live in the TopBar.
export function Sidebar(): JSX.Element | null {
  const user = useAuthStore((s) => s.user);
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  if (!user) return null;

  const canManageOrg = user.isOrgAdmin || user.role === 'SYS_ADMIN' || user.role === 'ADMIN';
  const showFinance = canFinance(user.role);
  const items: SideItem[] = [
    { to: '/', label: t.nav.dashboard, icon: 'dashboard' },
    { to: '/gsr', label: t.nav.gsrs, icon: 'description', matchPrefix: '/gsr' },
    { to: '/vendors', label: t.nav.vendors, icon: 'apartment', matchPrefix: '/vendors' },
    ...(showFinance ? [{ to: '/payments', label: t.nav.finance, icon: 'wallet', matchPrefix: '/payments' } as SideItem] : []),
    ...(canManageOrg ? [{ to: '/org-settings', label: t.orgSettings.navLabel, icon: 'settings', matchPrefix: '/org-settings' } as SideItem] : []),
  ];

  const isActive = (item: SideItem): boolean =>
    item.matchPrefix ? pathname.startsWith(item.matchPrefix) : pathname === item.to;

  const rowBase = 'flex items-center gap-4 rounded-full px-4 py-2.5 text-[0.95rem] transition-colors';

  return (
    <aside
      aria-label={t.nav.mainNav}
      className="hidden md:flex fixed start-0 top-16 bottom-0 z-30 w-64 flex-col gap-1 px-3 py-4 bg-[hsl(var(--chrome))] overflow-y-auto"
    >
      {/* Same audience as the requisitions list create button. */}
      <Link
        to="/gsr/new"
        className="mb-3 inline-flex w-fit items-center gap-3 rounded-2xl bg-[hsl(var(--tertiary-container))] text-[hsl(var(--on-tertiary-container))] py-3.5 ps-4 pe-6 shadow-sm hover:shadow-md transition-shadow"
      >
        <Icon name="add" size={24} />
        <span className="text-[0.95rem] font-medium">{t.gsr.newGsr}</span>
      </Link>

      <nav className="flex flex-col gap-0.5">
        {items.map((item) => {
          const active = isActive(item);
          return (
            <Link
              key={item.to}
              to={item.to}
              aria-current={active ? 'page' : undefined}
              className={[
                rowBase,
                active
                  ? 'bg-[hsl(var(--secondary-container))] text-[hsl(var(--on-secondary-container))] font-medium'
                  : 'text-foreground hover:bg-black/[0.05] dark:hover:bg-white/[0.06]',
              ].join(' ')}
            >
              <Icon name={item.icon} size={22} fill={active} className={active ? '' : 'text-muted-foreground'} />
              <span className="flex-1 truncate">{item.label}</span>
            </Link>
          );
        })}
      </nav>

      {/* Ecosystem jump */}
      <div className="mt-auto flex flex-col gap-0.5 pt-2">
        <a href="https://office.wapplify.app" className={`${rowBase} text-foreground hover:bg-black/[0.05] dark:hover:bg-white/[0.06]`}>
          <span className="grid size-[22px] shrink-0 place-items-center">
            <svg width="20" height="20" aria-hidden="true" viewBox="0 0 48 48">
              <rect width="48" height="48" rx="13.5" fill="#006A60" />
              <path d="M9 15.5 17.5 33 24 21.5" fill="none" stroke="#9EF2E4" strokeWidth={5.6} strokeLinecap="round" strokeLinejoin="round" />
              <path d="M24 21.5 30.5 33 39 15.5" fill="none" stroke="#fff" strokeWidth={5.6} strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </span>
          <span className="flex-1 truncate">ވެޕްލިފައި އޮފީސް</span>
          <Icon name="open_in_new" size={16} className="text-muted-foreground" />
        </a>
      </div>
    </aside>
  );
}
